// Il mio feed's calls to Loggione, as TanStack Query hooks: the user's feeds
// and the three ways to change them. The components never see the api.
//
// A rename, a new set of sources and a deletion take effect in the cached
// list at once; a refused call puts back the feed as it was (that feed only,
// so a change to another feed in flight is kept). A creation waits for the
// api: the new feed needs its id before it can be chosen (?feed=), so the
// editor stays open with «Salva» disabled until the answer.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, unwrap } from "../../api/client";
import { keys } from "../../api/keys";
import { applyFeedChange, type FeedChange, type NewFeed, type UserFeed, type UserFeeds } from "./adapt";

export type { FeedChange, NewFeed, UserFeed, UserFeeds };

/** GET /me/feeds: the caller's feeds, in order, each with its sources. */
export function useFeeds() {
  return useQuery({
    queryKey: keys.feeds,
    queryFn: async () => unwrap((await api().GET("/api/v1/me/feeds")).data),
  });
}

/** POST /me/feeds (201): the new feed joins the cached list, then the list is refetched. */
export function useCreateFeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: NewFeed) => unwrap((await api().POST("/api/v1/me/feeds", { body })).data),
    onSuccess: (feed) => {
      queryClient.setQueryData<UserFeeds>(keys.feeds, (old) => (old ? { feeds: [...old.feeds, feed] } : old));
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: keys.feeds });
    },
  });
}

type FeedContext = { previous: UserFeed | undefined };

/**
 * PATCH /me/feeds/{id}: the name, the sources (the whole set, or some with
 * add/remove), or both. The cached feed takes the change at once and goes
 * back as it was if the api refuses it. On settle the list is refetched;
 * with the sources changed, the feed's lists too.
 */
export function useChangeFeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, change }: { id: string; change: FeedChange }) =>
      unwrap(
        (await api().PATCH("/api/v1/me/feeds/{feed_id}", { params: { path: { feed_id: id } }, body: change })).data,
      ),
    onMutate: async ({ id, change }): Promise<FeedContext> => {
      await queryClient.cancelQueries({ queryKey: keys.feeds });
      const previous = queryClient.getQueryData<UserFeeds>(keys.feeds)?.feeds.find((f) => f.id === id);
      queryClient.setQueryData<UserFeeds>(keys.feeds, (old) =>
        old ? { feeds: old.feeds.map((f) => (f.id === id ? applyFeedChange(f, change) : f)) } : old,
      );
      return { previous };
    },
    onError: (_error, { id }, context) => {
      const previous = context?.previous;
      if (!previous) return;
      queryClient.setQueryData<UserFeeds>(keys.feeds, (old) =>
        old
          ? {
              feeds: old.feeds.map((f) => (f.id === id ? { ...f, name: previous.name, sources: previous.sources } : f)),
            }
          : old,
      );
    },
    onSettled: async (_data, _error, { id, change }) => {
      await queryClient.invalidateQueries({ queryKey: keys.feeds });
      if (change.sources != null || change.add?.length || change.remove?.length) {
        await queryClient.invalidateQueries({ predicate: (q) => keys.isFeedList(q.queryKey, id) });
      }
    },
  });
}

/**
 * DELETE /me/feeds/{id} (204): the feed leaves the cached list at once and
 * comes back, in its place, if the api refuses. Its lists leave the cache on settle.
 */
export function useDeleteFeed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api().DELETE("/api/v1/me/feeds/{feed_id}", { params: { path: { feed_id: id } } });
    },
    onMutate: async (id): Promise<FeedContext> => {
      await queryClient.cancelQueries({ queryKey: keys.feeds });
      const previous = queryClient.getQueryData<UserFeeds>(keys.feeds)?.feeds.find((f) => f.id === id);
      queryClient.setQueryData<UserFeeds>(keys.feeds, (old) =>
        old ? { feeds: old.feeds.filter((f) => f.id !== id) } : old,
      );
      return { previous };
    },
    onError: (_error, id, context) => {
      const previous = context?.previous;
      if (!previous) return;
      queryClient.setQueryData<UserFeeds>(keys.feeds, (old) =>
        old && !old.feeds.some((f) => f.id === id) ? { feeds: [...old.feeds, previous] } : old,
      );
    },
    onSettled: async (_data, _error, id) => {
      await queryClient.invalidateQueries({ queryKey: keys.feeds });
      queryClient.removeQueries({ predicate: (q) => keys.isFeedList(q.queryKey, id) });
    },
  });
}
